/**
 * RegistrantService.js
 *
 * Provides functions for managing registrants, including filtering, exporting,
 * user account actions (decline, block, unblock, delete, confirm), domain verification,
 * logs retrieval, and bulk user creation.
 * Uses the generic API utility for HTTP requests.
 */

import { api } from '@/services/httpCall';
import { GET, POST } from '@/shared/constants/httpMethods';
import { HANDS_ON_LAB, GET_IT_GIRL, CAPTURE_THE_FLAG, VIC_ONE } from '@/shared/constants/filters/invitations';

/**
 * Fetches registrants based on filter, pagination, search, and sorting options.
 * @param {Object} filter - Filter criteria.
 * @param {Object} pagination - Pagination info ({ page, pageSize }).
 * @param {string} search - Search query.
 * @param {Object} sorting - Sorting info ({ sortColumn, sortDirection }).
 * @returns {Promise<any|null>} Registrant data or null.
 */
export const getRegistrantsByFilter = async (filter, { page, pageSize }, search, { sortColumn, sortDirection }) => {
  let endpoint = '/admin/v2/registrants?';

  // Years filter
  for (let i = 0; i < filter.selectedYears.length; i += 1) {
    endpoint += i === 0 ? `year=${filter.selectedYears[i]}` : `&year=${filter.selectedYears[i]}`;
  }

  // Search filter
  if (search !== null && search !== '') endpoint += `&search=${search}`;

  // Pagination
  endpoint += `&length=${pageSize}`;
  endpoint += `&page=${page}`;

  // Sorting
  if (sortColumn) {
    endpoint += `&sortColumn=${sortColumn}`;
    endpoint += `&sortDirection=${sortDirection}`;
  }

  // Attendance confirmation
  if (filter.selectedAttendanceConfirmation) endpoint += `&attendanceConfirmation=${filter.selectedAttendanceConfirmation}`;

  // Side tracks
  if (filter.selectedSideTracks) {
    if (filter.selectedSideTracks.includes(HANDS_ON_LAB)) endpoint += '&IsHolRegistrant=true';
    if (filter.selectedSideTracks.includes(GET_IT_GIRL)) endpoint += '&IsGigRegistrant=true';
    if (filter.selectedSideTracks.includes(CAPTURE_THE_FLAG)) endpoint += '&IsCtfRegistrant=true';
    if (filter.selectedSideTracks.includes(VIC_ONE)) endpoint += '&IsVicOneRegistrant=true';
  }

  // Email, role, application status
  endpoint += `&emailConfirmed=${filter.selectedEmailStatus ?? ''}&role=${filter.selectedRole ?? ''}`;
  if (filter.selectedApplicationVerificationStatus) endpoint += `&applicationStatus=${filter.selectedApplicationVerificationStatus}`;

  // Industry, function, role function
  endpoint += `&industry=${filter.selectedIndustry ?? ''}&function=${filter.selectedFunction ?? ''}&roleFunction=${filter.selectedfunctionRole ?? ''}`;
  if (filter.selectedpreviousAttendanceStatus) endpoint += `&isLastYearAttendee=${filter.selectedpreviousAttendanceStatus ?? ''}`;

  // Account status
  if (filter.selectedAccountStatus) endpoint += `&status=${filter.selectedAccountStatus}`;

  const response = await api(GET, endpoint);
  return response.data ?? null;
};

/**
 * Fetches registrants for export based on filter criteria.
 * @param {Object} filter - Filter criteria.
 * @returns {Promise<any|null>} Exported registrant data or null.
 */
export const getExportedRegistrants = async (filter) => {
  let endpoint = '/admin/registrants?';

  // Years filter
  for (let i = 0; i < filter.selectedYears.length; i += 1) {
    endpoint += i === 0 ? `year=${filter.selectedYears[i]}` : `&year=${filter.selectedYears[i]}`;
  }

  // Email, role, application status
  endpoint += `&emailConfirmed=${filter.selectedEmailStatus ?? ''}&role=${filter.selectedRole ?? ''}`;
  if (filter.selectedApplicationVerificationStatus) {
    endpoint += `applicationStatus=${filter.selectedApplicationVerificationStatus}`;
  }

  // Attendance confirmation
  if (filter.selectedAttendanceConfirmation) endpoint += `&attendanceConfirmation=${filter.selectedAttendanceConfirmation}`;

  // Side tracks
  if (filter.selectedSideTracks) {
    if (filter.selectedSideTracks.includes(HANDS_ON_LAB)) endpoint += '&IsHolRegistrant=true';
    if (filter.selectedSideTracks.includes(GET_IT_GIRL)) endpoint += '&IsGigRegistrant=true';
    if (filter.selectedSideTracks.includes(CAPTURE_THE_FLAG)) endpoint += '&IsCtfRegistrant=true';
    if (filter.selectedSideTracks.includes(VIC_ONE)) endpoint += '&IsVicOneRegistrant=true';
  }

  // Industry, function, role function
  endpoint += `&industry=${filter.selectedIndustry ?? ''}&function=${filter.selectedFunction ?? ''}&roleFunction=${filter.selectedfunctionRole ?? ''}`;
  if (filter.selectedpreviousAttendanceStatus) endpoint += `&isLastYearAttendee=${filter.selectedpreviousAttendanceStatus ?? ''}`;

  // Account status
  if (filter.selectedAccountStatus) endpoint += `&status=${filter.selectedAccountStatus}`;

  const response = await api(GET, endpoint);
  return response.data ?? null;
};

/**
 * Declines a user's attendance.
 * @param {Object} params - Decline parameters ({ id, reason, remarks }).
 * @returns {Promise<string|number|null>} User ID if successful, otherwise null.
 */
export const declineUserAttendance = async ({ id, reason, remarks }) => {
  const response = await api(POST, '/admin/decline', { id, reason, remarks });
  if (response.statusCode === 200) return id;
  return null;
};

/**
 * Blocks a user account.
 * @param {Object} params - Block parameters ({ id, reason, remarks }).
 * @returns {Promise<string|number|null>} User ID if successful, otherwise null.
 */
export const blockUserAccount = async ({ id, reason, remarks }) => {
  const response = await api(POST, '/admin/block', { id, reason, remarks });
  if (response.statusCode === 200) return id;
  return null;
};

/**
 * Unblocks a user account.
 * @param {string|number} id - User ID.
 * @returns {Promise<string|number|null>} User ID if successful, otherwise null.
 */
export const unblockUserAccount = async (id) => {
  const response = await api(POST, '/admin/unblock', { id });
  if (response.statusCode === 200) return id;
  return null;
};

/**
 * Deletes a user account.
 * @param {Object} params - Delete parameters ({ id, remarks }).
 * @returns {Promise<string|number|null>} User ID if successful, otherwise null.
 */
export const deleteUserAccount = async ({ id, remarks }) => {
  const response = await api(POST, '/admin/purge', { id, remarks });
  if (response.statusCode === 200) return id;
  return null;
};

/**
 * Confirms a user's attendance.
 * @param {string|number} id - User ID.
 * @returns {Promise<string|number|null>} User ID if successful, otherwise null.
 */
export const confirmUserAttendance = async (id) => {
  const response = await api(POST, '/admin/confirm', { id });
  if (response.statusCode === 200) return id;
  return null;
};

/**
 * Verifies the domain of an email address.
 * @param {string} email - Email address.
 * @returns {Promise<boolean>} True if domain is valid, otherwise false.
 */
export const verifyDomain = async (email) => {
  try {
    const response = await api(GET, `/admin/verify-email-domain/${email}`);
    if (response.statusCode === 200) return true;
  } catch {
    return false;
  }
  return false;
};

/**
 * Fetches logs/events for a specific user by user ID.
 * @param {string|number} userId - User ID.
 * @returns {Promise<Array>} Array of logs if successful, otherwise empty array.
 */
export const getLogsByUserId = async (userId) => {
  const response = await api(GET, `/admin/events/${userId}`);
  if (response.statusCode === 200) return response.data;
  return [];
};

/**
 * Creates multiple users (guests) in bulk.
 * @param {Array} users - Array of user objects.
 * @returns {Promise<any|null>} Response data if successful, otherwise null.
 */
export const createUsers = async (users) => {
  const response = await api(POST, '/admin/v2/register-guest', { registrants: users, sendEmail: true, generateQr: true });
  if (response.statusCode === 200) return response.data;
  return null;
};
